/*
 *
 * PayByLinkStoreSelector
 *
 * @version 1.0.0
 * @author Marco Alessandroni
 * @since 08-Apr-2025
 */

import React, { useState, useMemo, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import PortalSearchBar from '@nexidigital/portalsearchbar'
import PortalCheckBox from '@nexidigital/portalcheckbox'
import PortalButton from '@nexidigital/portalbutton'
import { ChevronDown, Warning } from '@nexidigital/iconlibrary'
import formattedMessage from './languageModule'
import './StoreSelect.scss'
import { useIntl } from 'react-intl'
import { nullSafe } from 'Utility/globalMethods'
import { useMobileDetect } from 'Hoc/ListenerProvider'

const StoreSelect = ({
  storeOptions,
  selectedStores,
  onChangeSelectedStores,
  selectorLabel,
  isMultiSelection,
  error,
  errorText,
  onDropdownClose
}) => {
  const [isDropdownOpen, setDropdownOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const intl = useIntl()

  const selectorRef = useRef(null)
  const hadInteraction = useRef(false)

  const { isPhone } = useMobileDetect()

  useEffect(() => {
    function handleClickOutside (event) {
      if (selectorRef.current && !selectorRef.current.contains(event.target)) {
        if (isDropdownOpen) {
          setDropdownOpen(false)
          // Notifica chiusura senza selezione
          if (onDropdownClose && hadInteraction.current) {
            onDropdownClose()
          }
          hadInteraction.current = false
        }
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [selectorRef, isDropdownOpen, onDropdownClose])

  const filteredStores = useMemo(
    () =>
      storeOptions.filter(
        (store) => store.label.toLowerCase().includes(searchQuery.toLowerCase()) || store.code.includes(searchQuery)
      ),
    [storeOptions, searchQuery]
  )

  const options = isMultiSelection
    ? filteredStores.map((store) => ({
      id: store.id,
      value: store.id,
      label: (
        <div className={`storeInfo ${selectedStores.includes(store.id) ? 'selected' : ''}`}>
          <h1 className='storeLabel'>{store.label}</h1>
          <div className='storeCode'>
            <span className='storeCodeLabel'>{formattedMessage.storeCodeLabel}</span>
            <span className='storeCodeValue'>{store.code}</span>
          </div>
        </div>
      ),
      checked: selectedStores.includes(store.id)
    }))
    : filteredStores.map((store) => ({
      id: store.id,
      value: store.id,
      label: (
        <div className='storeInfo'>
          <h1 className='storeLabel'>{store.label}</h1>
          <div className='storeCode'>
            <span className='storeCodeLabel'>{formattedMessage.storeCodeLabel}</span>
            <span className='storeCodeValue'>{store.code}</span>
          </div>
        </div>
      )
    }))

  const handleCheckboxChange = ({ id, checked, options: updatedOptions }) => {
    if (id === 'selectAll') {
      if (checked) {
        // Seleziona tutti gli elementi filtrati, mantenendo quelli già selezionati non filtrati
        const filteredIds = updatedOptions.map((o) => o.value)
        const nonFilteredSelectedIds = selectedStores.filter(
          (storeId) => !filteredStores.some((store) => store.id === storeId)
        )
        onChangeSelectedStores([...nonFilteredSelectedIds, ...filteredIds])
      } else {
        // Deseleziona tutti gli elementi filtrati, mantenendo quelli selezionati non filtrati
        const filteredIds = updatedOptions.map((o) => o.value)
        const remainingSelected = selectedStores.filter((storeId) => !filteredIds.includes(storeId))
        onChangeSelectedStores(remainingSelected)
      }
    } else {
      // Gestisce la selezione/deselezione di un singolo elemento
      if (checked) {
        // Aggiunge l'elemento alla selezione se non è già presente
        if (!selectedStores.includes(id)) {
          onChangeSelectedStores([...selectedStores, id])
        }
      } else {
        // Rimuove l'elemento dalla selezione
        onChangeSelectedStores(selectedStores.filter((storeId) => storeId !== id))
      }
    }
  }

  const handleOptionClick = (id) => {
    hadInteraction.current = false // Reset perché abbiamo selezionato un valore
    onChangeSelectedStores(id)
    setDropdownOpen(false)
  }

  const renderSearchBar = (
    <div className='searchWrapper'>
      <PortalSearchBar
        id='store-filter'
        placeholder={intl.formatMessage({ id: 't.app.paybylink.filtersModal.searchStore' })}
        value={searchQuery}
        onChange={(value) => setSearchQuery(value)}
        handleIconClick={() => setSearchQuery('')}
        disableSendAnalytics
        cleanField
        liveSearch
      />
    </div>
  )

  const renderMultiOptions = (
    <>
      {renderSearchBar}
      <div className='listWrapper'>
        <div className='checkboxHeader'>
          <PortalCheckBox
            options={options}
            selectAllEnabled
            selectAllDisabled={filteredStores.length === 0}
            selectAllLabel={
              <div className='selectAllRow'>
                <div className='selectAllInfo'>
                  <span className={`${selectedStores.length > 0 ? 'selectAllActive' : ''}`}>
                    {searchQuery ? formattedMessage.selectAllResults : formattedMessage.selectAll}
                  </span>

                  {selectedStores.length > 0 && (
                    <span className='selectedAllText'>
                      {selectedStores.length} {selectedStores.length > 1 ? formattedMessage.selected : formattedMessage.singleSelected}
                    </span>
                  )}
                </div>
                {selectedStores.length > 0 && (
                  <PortalButton
                    textButton
                    size='small'
                    className='resetSelection'
                    id='menuContentCloseIcon'
                    label={isPhone ? formattedMessage.reset : formattedMessage.resetSelection}
                    onClick={() => onChangeSelectedStores([])}
                  />
                )}
              </div>
            }
            onChange={handleCheckboxChange}
          />
          {filteredStores.length === 0 && searchQuery && <div className='noResults'>{formattedMessage.noResults}</div>}
        </div>
      </div>
    </>
  )

  const renderOptions = (
    <div className='optionsWrapper' role='listbox'>
      {options.map((option) => (
        <div
          className='option'
          key={option.id}
          role='option'
          tabIndex={0}
          onClick={() => handleOptionClick(option.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleOptionClick(option.id)
            }
          }}
          aria-selected={selectedStores === option.id}
        >
          {option.label}
        </div>
      ))}
    </div>
  )

  return (
    <div className='storeSelector' ref={selectorRef}>
      <div className='dropdownSelector'>
        <span className='dropdownLabel'>{selectorLabel}</span>
        <div
          role='button'
          tabIndex={0}
          onClick={() => {
            const newState = !isDropdownOpen
            if (newState) {
              hadInteraction.current = true
            } else if (onDropdownClose && hadInteraction.current) {
              onDropdownClose()
              hadInteraction.current = false
            }
            setDropdownOpen(newState)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              const newState = !isDropdownOpen
              if (newState) {
                hadInteraction.current = true
              } else if (onDropdownClose && hadInteraction.current) {
                onDropdownClose()
                hadInteraction.current = false
              }
              setDropdownOpen(newState)
            }
          }}
          aria-expanded={isDropdownOpen}
          aria-label='Toggle dropdown'
          className={`dropdownInput ${isDropdownOpen ? 'openDrop' : ''} ${error && 'error'}`}
        >
          {isMultiSelection
            ? (
              <span
                className={`dropdownPlaceholder ${selectedStores.length === 0 || isDropdownOpen ? 'placeholder' : ''}`}
              >
                {selectedStores.length === 0 || isDropdownOpen
                  ? formattedMessage.selectStorePlaceholder
                  : selectedStores.length === 1
                    ? storeOptions.find((opt) => opt.id === selectedStores[0])?.label || selectedStores[0]
                    : `${selectedStores.length} stabilimenti`}
              </span>
              )
            : (
              <>
                <span
                  className={`dropdownPlaceholder ${nullSafe(() => selectedStores, []).length > 0 ? '' : 'placeholder'}`}
                >
                  {selectedStores
                    ? storeOptions.find((opt) => opt.id === selectedStores)?.label
                    : formattedMessage.selectStorePlaceholder}
                </span>
                {error && (
                  <div className='warningIcon'>
                    <Warning size='24' type='line' />
                  </div>
                )}
              </>
              )}
          <div className={`chevronWrapper ${isDropdownOpen ? 'open' : ''}`}>
            <ChevronDown size='24' type='line' />
          </div>
        </div>
        {error && errorText && <span className='errorText'>{errorText}</span>}
      </div>

      {isDropdownOpen && <div className='dropdownPanel'>{isMultiSelection ? renderMultiOptions : renderOptions}</div>}
    </div>
  )
}

StoreSelect.propTypes = {
  storeOptions: PropTypes.array.isRequired,
  selectedStores: PropTypes.array.isRequired,
  onChangeSelectedStores: PropTypes.func.isRequired,
  selectorLabel: PropTypes.string,
  /** This prop is expected to be a boolean.
   * If it's true, the PortalSelect component will accept multiple selections.
   * If it's false or not provided, the PortalSelect component will not accept multiple selections. */
  isMultiSelection: PropTypes.bool,
  /** This prop is expected to be a boolean.
   * If it's true, the PortalSelect component will have an error.
   * If it's false or not provided, the PortalSelect component will not have an error. */
  error: PropTypes.bool,
  /** This prop is expected to be a string. It represents the error text of the select. */
  errorText: PropTypes.string,
  /** This prop is expected to be a function. It is called when dropdown closes without selection */
  onDropdownClose: PropTypes.func
}

StoreSelect.defaultProps = {
  storeOptions: [],
  selectedStores: [],
  onChangeSelectedStores: () => {},
  selectorLabel: formattedMessage.selectStoreLabel,
  isMultiSelection: false
}

export default StoreSelect
